// Pure data — no icon component values, so this stays server-safe.

export const QUICK_ANSWER =
  "Choose Proxidize for separate, predictable mobile ($2/GB) and residential ($1/GB) pricing with no shared-pool math. Choose NodeMaven if you want one combined pool for both proxy types, its IP Quality Filter and published success-rate claims, a very small paid test, or ISP proxies.";

export const KEY_TAKEAWAYS = [
  "NodeMaven pools residential and mobile into one bandwidth plan. Proxidize prices and buys mobile and residential separately, at flat rates.",
  "Proxidize residential beats every NodeMaven tier we checked. Flat $1/GB versus NodeMaven's $2.20–4.25/GB range, which only reaches its cheapest rate at 1,000 GB/month.",
  "NodeMaven's smallest paid test is far below Proxidize's minimum. $3.50 for 750 MB, versus Proxidize's 25 GB entry point.",
  "NodeMaven advertises an IP Quality Filter with published accuracy claims. 99.54% success rate and 95% clean rate — a discrete feature Proxidize doesn't publish an equivalent for.",
  "NodeMaven sessions can stay sticky far longer on residential. Up to 7 days, versus Proxidize's sticky-or-random per access point with no published maximum.",
  "NodeMaven also sells ISP proxies. From $2.99/IP at bulk pricing — a category Proxidize doesn't offer yet.",
];

export interface VerdictCard {
  provider: "proxidize" | "nodemaven";
  title: string;
  points: string[];
}

export const VERDICT_CARDS: VerdictCard[] = [
  {
    provider: "proxidize",
    title: "Choose Proxidize if you need",
    points: [
      "Separate, clearly priced mobile ($2/GB) and residential ($1/GB) plans — no shared-pool math to work through",
      "Premium pooled mobile IPs with city and ASN targeting",
      "Residential proxies in 195+ countries with unused bandwidth rollover",
      "Manual/bulk rotation, an API rotation URL, and custom interval control",
      "Self-serve setup: credentials in under a minute, free sign-up, no sales call",
    ],
  },
  {
    provider: "nodemaven",
    title: "Choose NodeMaven if you need",
    points: [
      "Residential and mobile IPs pooled under the same bandwidth plan",
      "An IP Quality Filter that screens IPs before assigning them, plus a published 95% clean-rate and 99.54% success-rate claim",
      "Sticky sessions up to 7 days on residential, up to 24 hours on mobile",
      "Up to 10% cashback on consumed bandwidth, plus rollover to the next billing period",
      "ISP proxies from $2.99/IP with unlimited traffic and UDP support",
    ],
  },
];

export interface ComparisonRow {
  feature: string;
  proxidize: string | boolean;
  nodemaven: string | boolean;
}

export const COMPARISON_ROWS: ComparisonRow[] = [
  {
    feature: "Best for",
    proxidize: "Straightforward mobile and residential proxy access at flat rates",
    nodemaven: "Quality-filtered residential/mobile traffic pooled under one plan",
  },
  {
    feature: "Product breadth",
    proxidize: "Mobile and residential live; ISP and datacenter coming soon",
    nodemaven: "Residential, Mobile, ISP, Static Residential, Rotating Residential, IPv6, SOCKS5",
  },
  {
    feature: "Pool structure",
    proxidize: "Mobile and residential purchased and priced separately",
    nodemaven: "Residential and mobile share the same bandwidth plan and pricing",
  },
  {
    feature: "Mobile pricing",
    proxidize: "Flat $2/GB (plans from 25 GB to 10,000 GB)",
    nodemaven: "Combined pool, volume-tiered from $4.25/GB (2 GB) down to $2.20/GB (1,000 GB)",
  },
  {
    feature: "Residential pricing",
    proxidize: "Flat $1/GB (plans from 25 GB to 10,000 GB)",
    nodemaven: "Same combined-pool pricing as mobile — no separate residential rate",
  },
  {
    feature: "Smallest paid test",
    proxidize: "25 GB mobile plan ($50) or 25 GB residential plan ($25)",
    nodemaven: "$3.50 for 750 MB",
  },
  {
    feature: "Lowest monthly plan",
    proxidize: "$50 (25 GB mobile) or $25 (25 GB residential)",
    nodemaven: "$8.50 for 2 GB ($4.25/GB)",
  },
  {
    feature: "Largest published plan",
    proxidize: "10,000 GB ($20,000 mobile / $9,000 residential)",
    nodemaven: "1,000 GB ($2,200, i.e. $2.20/GB)",
  },
  {
    feature: "Pay-as-you-go option",
    proxidize: "Not applicable — monthly per-GB plans only",
    nodemaven:
      "Same 9 volume tiers, priced higher than monthly plans: $5.00/GB (2 GB) down to $2.45/GB (1,000 GB)",
  },
  {
    feature: "IP pool size",
    proxidize: "Not publicly broken out by IP count",
    nodemaven: "30M+ residential IPs and 250K+ mobile IPs across 190+ countries",
  },
  {
    feature: "IP-quality feature",
    proxidize: "Premium mobile IP sourcing, published mobile success-rate claim",
    nodemaven: "Dedicated IP Quality Filter — 99.54% success rate, 95% clean rate advertised",
  },
  {
    feature: "Sticky sessions",
    proxidize: "Sticky or random per access point",
    nodemaven: "Up to 7 days (residential), up to 24 hours (mobile)",
  },
  {
    feature: "Targeting",
    proxidize: "Mobile: city, ASN. Residential: city, ISP",
    nodemaven: "Country, city, ISP, and ZIP-level targeting in 150+ locations",
  },
  {
    feature: "Traffic rollover",
    proxidize: "Residential bandwidth rolls over; mobile per-GB bandwidth expires after 1 month",
    nodemaven: "Unused bandwidth rolls over to the next billing period",
  },
  {
    feature: "Cashback",
    proxidize: false,
    nodemaven: "Up to 10% cashback on consumed bandwidth advertised",
  },
  {
    feature: "ISP (static residential) proxies",
    proxidize: "Not yet offered",
    nodemaven:
      "$4.99/IP (30 days) list price, down to $2.99/IP at 10+ proxies; 90-day terms available",
  },
  {
    feature: "Protocols",
    proxidize: "HTTP(S), SOCKS5, HTTP/3 + QUIC",
    nodemaven: "HTTP, SOCKS5 (residential/mobile); adds UDP on ISP proxies",
  },
  { feature: "Self-serve signup", proxidize: true, nodemaven: true },
  {
    feature: "Free / low-cost testing",
    proxidize: "Free sign-up, no credit card",
    nodemaven: "$3.50 paid trial (750 MB)",
  },
];

export interface UseCaseFit {
  useCase: string;
  fit: "Proxidize" | "NodeMaven" | "Both";
  reason: string;
}

export const USE_CASE_FITS: UseCaseFit[] = [
  {
    useCase: "Buyers who want separate, predictable mobile and residential pricing",
    fit: "Proxidize",
    reason:
      "A flat $2/GB for mobile and $1/GB for residential — no shared-pool math to work through before you know your bill.",
  },
  {
    useCase: "Very small or one-off paid testing",
    fit: "NodeMaven",
    reason:
      "$3.50 for 750 MB beats Proxidize's 25 GB / $50 minimum entry point for a tiny paid test.",
  },
  {
    useCase: "Buyers who want IP quality filtering as a built-in feature",
    fit: "NodeMaven",
    reason:
      "Its IP Quality Filter screens addresses in real time before assigning them — a discrete feature Proxidize doesn't publish an equivalent for.",
  },
  {
    useCase: "Lower-cost residential bandwidth at scale",
    fit: "Proxidize",
    reason:
      "A flat $1/GB from 25 GB beats every one of NodeMaven's published tiers, including its cheapest ($2.20/GB, reached only at 1,000 GB/month) — and Proxidize's rate doesn't depend on hitting a volume threshold.",
  },
  {
    useCase: "Long-lived sticky sessions for account-based workflows",
    fit: "NodeMaven",
    reason:
      "Residential sessions can stay sticky for up to 7 days, versus Proxidize's sticky-or-random per access point with no published maximum duration.",
  },
  {
    useCase: "One combined pool for mobile and residential traffic",
    fit: "NodeMaven",
    reason:
      "The same plan and pricing covers both proxy types; Proxidize prices and buys mobile and residential separately.",
  },
  {
    useCase: "Bandwidth that doesn't go to waste",
    fit: "Both",
    reason:
      "NodeMaven rolls over unused bandwidth on every plan; Proxidize rolls over residential bandwidth, though its mobile per-GB bandwidth expires after one month.",
  },
];

export interface MigrationStep {
  title: string;
  description: string;
}

export const MIGRATION_STEPS: MigrationStep[] = [
  {
    title: "Create a free account",
    description: "Sign up for free: no credit card, no sales call.",
  },
  {
    title: "Pick your per-GB plan",
    description:
      "Per-GB gives flexible access to the full pool, billed by data. Choose the monthly volume that fits your mobile or residential usage.",
  },
  {
    title: "Create an access point",
    description:
      "Choose a city or random rotation and set sticky or random IP behavior — the same knobs you tuned in NodeMaven's dashboard.",
  },
  {
    title: "Copy your credentials",
    description:
      "Credentials are generated live in HTTP or SOCKS5 format, with auto-generated cURL examples and formatted proxy list exports.",
  },
  {
    title: "Test before switching traffic",
    description:
      "Run your existing scraper or browser profile against the new endpoint with a small job and compare success rates side by side.",
  },
  {
    title: "Monitor usage in real time",
    description:
      "The built-in analytics dashboard shows bandwidth trends, top domains, and a live connection log per access point.",
  },
];

export const MIGRATION_CURL = `curl -x "http://USERNAME:PASSWORD@HOST:PORT" https://ipinfo.io`;
